import { BADGE_CATEGORY, BadgeDefinition } from "@shared/models";

/**
 * One rung of the flame ladder, as derived from the badge catalog — name and threshold only. No
 * `index` field: nothing here reads catalog position as an index. `current_tier.index`, carried on
 * the flame progress response itself, is the only index this feature ever positions a pip from (see
 * `FlameProgress.tsx`'s own `buildFlamePipStates`) — never a position recomputed from this array.
 */
export interface FlameTier {
  name: string;
  threshold: number;
}

const toFlameTier = (definition: BadgeDefinition): FlameTier | null =>
  definition.category === BADGE_CATEGORY.FLAME && definition.threshold !== null
    ? { name: definition.name, threshold: definition.threshold }
    : null;

/**
 * Derives the full flame ladder from the badge catalog (lifetime-flame-rebuild plan), ordered by
 * threshold ascending. This is the ONE place the ladder's LENGTH and its top rung's threshold come
 * from — the flame progress response (`FlameProgress` model) only ever carries `current_tier`/
 * `next_tier`, two rungs of the ladder, never the full list or its size. No `is_active` filter,
 * mirroring `ValueLadderCards`' own `countCatalogRungs`: a ladder's length is structural, and hiding a
 * rung from the catalog must not shorten the track.
 *
 * @param badgeDefinitions - the full badge catalog, or `null` if it hasn't loaded / failed to load.
 * @returns the flame tiers in ascending threshold order; `[]` when the catalog isn't available.
 */
export const getFlameLadderTiers = (badgeDefinitions: BadgeDefinition[] | null): FlameTier[] =>
  badgeDefinitions
    ? badgeDefinitions
        .map(toFlameTier)
        .filter((tier): tier is FlameTier => tier !== null)
        .sort((a, b) => a.threshold - b.threshold)
    : [];
